import { httpDelete, httpGet, httpPatch, httpPost, httpPut } from './httpClient'
import { useAuthStore } from '@/modules/authStore/stores/authStore'
import type {
  ChatDiscussionMode,
  ChatTopicsDisplayMode,
  IChat,
  IChatTopic,
} from '@/modules/chat-matrix-fast/types/IChat'
import { BASE_URL } from '@/environment/environment'
import { isNonHumanDialogTitle, pickRealChatTitle, resolveChatIcon } from '@/modules/chat-matrix-fast/constants/chat'
import { indexEmployeeDisplayName } from '@/modules/chat-matrix-fast/services/employeeLookup'
import type { ChatCatalog } from '@/modules/chat-matrix-fast/types/chatCatalog'
import type { EventChatAppLabel, EventChatModelName } from '@/modules/chat-matrix-fast/types/eventChatEntity'

const MATRIX_CHAT_API = `${BASE_URL}/api/matrix_chat/chat`

export class AuthenticationError extends Error {
  constructor(message: string = 'Пользователь не авторизован') {
    super(message)
    this.name = 'AuthenticationError'
  }
}

function checkAuth(): void {
  const authStore = useAuthStore()
  if (!authStore.isAuthenticated) {
    throw new AuthenticationError()
  }
}

function normalizeChatList(data: unknown): IChat[] {
  if (Array.isArray(data)) {
    return data
  }

  if (!data || typeof data !== 'object') {
    return []
  }

  const payload = data as { results?: unknown; data?: unknown }

  if (Array.isArray(payload.results)) {
    return payload.results
  }

  if (Array.isArray(payload.data)) {
    return payload.data
  }

  return []
}

function normalizeTopicsList(data: unknown): IChatTopic[] {
  if (Array.isArray(data)) {
    return data as IChatTopic[]
  }

  if (!data || typeof data !== 'object') {
    return []
  }

  const payload = data as { results?: unknown }
  if (Array.isArray(payload.results)) {
    return payload.results as IChatTopic[]
  }

  return []
}

function normalizeDialogMember(member: IChat['dialog_member']): IChat['dialog_member'] {
  if (!member) {
    return member
  }

  const fullName = member.full_name?.trim()
  if (!fullName) {
    return member
  }

  const parts = fullName.split(/\s+/).filter(Boolean)
  const hasNameParts = Boolean(member.first_name?.trim() || member.last_name?.trim())
  if (!hasNameParts) {
    return {
      ...member,
      last_name: parts[0] || member.last_name,
      first_name: parts[1] || member.first_name,
      middle_name: member.middle_name || parts.slice(2).join(' ') || undefined,
    }
  }

  if (!member.middle_name?.trim() && parts.length >= 3) {
    return {
      ...member,
      middle_name: parts.slice(2).join(' ') || undefined,
    }
  }

  return member
}

function normalizeChat(chat: IChat): IChat {
  const dialogMember = normalizeDialogMember(chat.dialog_member)
  const normalized = {
    ...chat,
    dialog_member: dialogMember,
    icon: resolveChatIcon(
      chat.icon,
      chat.display_avatar_url,
      chat.avatar_url,
      dialogMember?.avatar,
    ),
    description: chat.description ?? '',
    members: chat.members ?? [],
    owner: chat.owner ?? chat.created_by?.id ?? '',
  }

  const title = pickRealChatTitle(normalized) || normalized.title
  if (
    dialogMember
    && title
    && !isNonHumanDialogTitle(title, chat.matrix_room_id, dialogMember.matrix_user_id)
  ) {
    indexEmployeeDisplayName({
      id: dialogMember.id,
      matrixUserId: dialogMember.matrix_user_id,
      fullName: title,
    })
  }

  return {
    ...normalized,
    title,
  }
}

export interface ChatMemberResponse {
  user: {
    id: string
    first_name: string
    last_name: string
    middle_name: string
    avatar: string
    department_name: string
    position_name: string
  }
  user_uuid?: string
  avatar: string
  online: boolean
  is_admin: boolean
  joined_at: string
  membership?: 'join' | 'invite'
}

export interface ChatEventCreateResponse {
  id: number
  type: string
  title: string
  icon: string
  description: string
  total_members: number
  matrix_room_id?: string
  event_url?: string | null
  created_by: {
    id: string
    first_name: string
    last_name: string
    middle_name?: string
    avatar?: string | null
    department_name?: string
    position_name?: string
  }
}

export interface MessageReader {
  id: string | number
  user: {
    id: string | number
    first_name: string
    last_name: string
    middle_name?: string
    avatar?: string | null
    department_name?: string
    position_name?: string
  }
  avatar?: string | null
  read_at: string
}

export interface CreateGroupPayload {
  title: string
  description?: string
  avatar_url?: string | null
  members_ids?: string[]
  discussion_mode?: ChatDiscussionMode
  topics_display_mode?: ChatTopicsDisplayMode
}

export interface ChatResolveResponse {
  id: number
  type: string
  matrix_room_id?: string | null
  space_room_id?: string | null
  discussion_mode?: ChatDiscussionMode
  topic?: IChatTopic | null
  icon?: string | null
  avatar_url?: string | null
  display_avatar_url?: string | null
  dialogMember?: {
    id?: string
    matrix_user_id?: string | null
    first_name?: string
    last_name?: string
    middle_name?: string
    full_name?: string
    avatar?: string | null
    position_name?: string
    department_name?: string
  } | null
}

interface BackendChatMemberItem {
  user: ChatMemberResponse['user'] & { matrix_user_id?: string | null }
  is_admin: boolean
  joined_at: string
}

interface MembersPageResponse {
  results?: BackendChatMemberItem[]
  next?: string | null
  count?: number
}

function extractCursorFromUrl(url: string): string | null {
  try {
    return new URL(url).searchParams.get('cursor')
  } catch {
    const match = url.match(/[?&]cursor=([^&]+)/)
    return match ? decodeURIComponent(match[1]) : null
  }
}

function mapBackendMember(item: BackendChatMemberItem): ChatMemberResponse {
  return {
    user: item.user,
    user_uuid: item.user.matrix_user_id ?? undefined,
    avatar: item.user.avatar || '',
    online: false,
    is_admin: item.is_admin,
    joined_at: item.joined_at,
    membership: 'join',
  }
}

export class ChatApiService {
  static async fetchChatCatalog(): Promise<ChatCatalog> {
    checkAuth()
    const data = await httpGet<ChatCatalog>(`${BASE_URL}/api/matrix/rooms/`)
    if (
      !data
      || !Array.isArray(data.chats)
      || !Array.isArray(data.directories)
      || !Array.isArray(data.root_directory_ids)
      || !Array.isArray(data.ungrouped_chat_ids)
    ) {
      throw new Error('Некорректный ответ каталога чатов')
    }

    return { ...data, chats: data.chats.map(normalizeChat) }
  }

  static async fetchChatList(types: string): Promise<IChat[]> {
    checkAuth()

    const params = new URLSearchParams({ types })
    const url = `${MATRIX_CHAT_API}/?${params.toString()}`
    const data = await httpGet<unknown>(url)

    return normalizeChatList(data).map(normalizeChat)
  }

  static async fetchChatById(chatId: number | string): Promise<IChat> {
    checkAuth()
    const chat = await httpGet<IChat>(`${MATRIX_CHAT_API}/${chatId}/`)
    return normalizeChat(chat)
  }

  static async createOrGetDialog(userId: string): Promise<IChat> {
    checkAuth()
    const chat = await httpPost<IChat>(`${MATRIX_CHAT_API}/dialog/`, { user_id: userId })
    return normalizeChat(chat)
  }

  static async createGroup(payload: CreateGroupPayload): Promise<IChat> {
    checkAuth()
    const chat = await httpPost<IChat>(`${MATRIX_CHAT_API}/group/`, payload)
    return normalizeChat(chat)
  }

  static async createChannel(payload: CreateGroupPayload): Promise<IChat> {
    checkAuth()
    const chat = await httpPost<IChat>(`${MATRIX_CHAT_API}/channel/`, payload)
    return normalizeChat(chat)
  }

  static async updateChatSettings(
    chatId: number | string,
    payload: {
      title?: string
      description?: string
      topics_display_mode?: ChatTopicsDisplayMode
    },
  ): Promise<IChat> {
    checkAuth()
    const chat = await httpPatch<IChat>(`${MATRIX_CHAT_API}/${chatId}/settings/`, payload)
    return normalizeChat(chat)
  }

  static async addMembers(chatId: number | string, userIds: string[]): Promise<void> {
    checkAuth()
    await httpPost(`${MATRIX_CHAT_API}/${chatId}/add-members/`, { user_ids: userIds })
  }

  static async fetchMembers(
    chatId: number | string,
    options?: { cursor?: string; pageSize?: number },
  ): Promise<{ members: ChatMemberResponse[]; nextCursor: string | null; count: number | null }> {
    checkAuth()

    const params: Record<string, string | number> = {}
    if (options?.cursor) {
      params.cursor = options.cursor
    }
    if (options?.pageSize) {
      params.page_size = options.pageSize
    }

    const data = await httpGet<MembersPageResponse>(
      `${MATRIX_CHAT_API}/${chatId}/members/`,
      { params: Object.keys(params).length ? params : undefined },
    )

    const members = (data.results ?? []).map(mapBackendMember)
    return {
      members,
      nextCursor: data.next ? extractCursorFromUrl(data.next) : null,
      count: data.count ?? null,
    }
  }

  static async uploadAvatar(chatId: number | string, file: File): Promise<IChat> {
    checkAuth()
    const formData = new FormData()
    formData.append('file', file)
    const chat = await httpPut<IChat>(`${MATRIX_CHAT_API}/${chatId}/avatar/`, formData)
    return normalizeChat(chat)
  }

  static async deleteAvatar(chatId: number | string): Promise<IChat> {
    checkAuth()
    await httpDelete(`${MATRIX_CHAT_API}/${chatId}/avatar/`)
    return ChatApiService.fetchChatById(chatId)
  }

  static async removeMember(chatId: number | string, userId: string): Promise<void> {
    checkAuth()
    await httpDelete(`${MATRIX_CHAT_API}/${chatId}/remove-member/`, {
      params: { user_id: userId },
    })
  }

  static async setAdmin(
    chatId: number | string,
    userId: string,
    isAdmin: boolean,
  ): Promise<void> {
    checkAuth()
    await httpPost(`${MATRIX_CHAT_API}/${chatId}/set-admin/`, {
      user_id: userId,
      is_admin: isAdmin,
    })
  }

  static async leaveChat(chatId: number | string): Promise<void> {
    checkAuth()
    await httpPost(`${MATRIX_CHAT_API}/${chatId}/leave/`)
  }

  static async pinChat(chatId: number | string): Promise<IChat> {
    checkAuth()
    const chat = await httpPost<IChat>(`${MATRIX_CHAT_API}/${chatId}/pin/`)
    return normalizeChat(chat)
  }

  static async unpinChat(chatId: number | string): Promise<IChat> {
    checkAuth()
    const chat = await httpDelete<IChat | null | Record<string, never>>(
      `${MATRIX_CHAT_API}/${chatId}/pin/`,
    )
    if (chat && typeof chat === 'object' && 'id' in chat) {
      return normalizeChat(chat as IChat)
    }

    return ChatApiService.fetchChatById(chatId)
  }

  /** POST /{id}/join-invite/ — вступить в группу по ссылке-приглашению */
  static async joinByInvite(chatId: number | string): Promise<IChat> {
    checkAuth()
    const chat = await httpPost<IChat>(`${MATRIX_CHAT_API}/${chatId}/join-invite/`)
    return normalizeChat(chat)
  }

  static async deleteChat(chatId: number | string): Promise<void> {
    checkAuth()
    await httpDelete(`${MATRIX_CHAT_API}/${chatId}/`)
  }

  static async enableTopics(chatId: number | string): Promise<IChat> {
    checkAuth()
    const chat = await httpPost<IChat>(`${MATRIX_CHAT_API}/${chatId}/enable-topics/`)
    return normalizeChat(chat)
  }

  /** POST /{id}/retry-structure/ — repair зависшего provisioning (доступно любому участнику) */
  static async retryStructure(chatId: number | string): Promise<IChat> {
    checkAuth()
    const chat = await httpPost<IChat>(`${MATRIX_CHAT_API}/${chatId}/retry-structure/`)
    return normalizeChat(chat)
  }

  static async requestMembership(chatId: number | string, roomId: string, retry = false) {
    checkAuth()
    return httpPost<import('./membershipProtocol').MatrixMembershipStatus>(`${MATRIX_CHAT_API}/${chatId}/membership/`, {
      room_id: roomId,
      retry,
    })
  }

  static async fetchMembershipStatus(chatId: number | string, roomId: string) {
    checkAuth()
    return httpGet<import('./membershipProtocol').MatrixMembershipStatus>(`${MATRIX_CHAT_API}/${chatId}/membership/`, {
      params: { room_id: roomId },
    })
  }

  static async syncPowerLevels(chatId: number | string): Promise<void> {
    checkAuth()
    await httpPost(`${MATRIX_CHAT_API}/${chatId}/sync-power-levels/`)
  }

  static async fetchTopics(
    chatId: number | string,
    options?: { includeArchived?: boolean },
  ): Promise<IChatTopic[]> {
    checkAuth()
    const params = options?.includeArchived ? { include_archived: 'true' } : undefined
    const data = await httpGet<unknown>(`${MATRIX_CHAT_API}/${chatId}/topics/`, { params })
    return normalizeTopicsList(data)
  }

  static async createTopic(chatId: number | string, title: string): Promise<IChatTopic> {
    checkAuth()
    return httpPost<IChatTopic>(`${MATRIX_CHAT_API}/${chatId}/topics/`, { title })
  }

  static async updateTopic(
    chatId: number | string,
    topicId: number,
    title: string,
  ): Promise<IChatTopic> {
    checkAuth()
    return httpPatch<IChatTopic>(`${MATRIX_CHAT_API}/${chatId}/topics/${topicId}/`, { title })
  }

  static async archiveTopic(chatId: number | string, topicId: number): Promise<IChatTopic> {
    checkAuth()
    return httpPost<IChatTopic>(`${MATRIX_CHAT_API}/${chatId}/topics/${topicId}/archive/`)
  }

  /** POST /{id}/topics/{topicId}/retry-provision/ — повторить создание Matrix-комнаты темы */
  static async retryTopicProvision(
    chatId: number | string,
    topicId: number,
  ): Promise<IChatTopic> {
    checkAuth()
    return httpPost<IChatTopic>(
      `${MATRIX_CHAT_API}/${chatId}/topics/${topicId}/retry-provision/`,
    )
  }

  static async resolveRoom(roomId: string): Promise<IChat> {
    checkAuth()
    const data = await httpGet<IChat>(`${MATRIX_CHAT_API}/resolve/`, {
      params: { room_id: roomId },
    })
    return normalizeChat(data)
  }

  static async createEventChat(payload: {
    app_label: EventChatAppLabel
    model: EventChatModelName
    object_id: number
  }): Promise<ChatEventCreateResponse> {
    checkAuth()

    return httpPost<ChatEventCreateResponse>(
      `${MATRIX_CHAT_API}/event-chat/`,
      payload,
    )
  }

  static async prepareForward(payload: {
    source_room_id: string
    source_event_id: string
    destination_chat_id: number
    destination_topic_id?: number | null
    attribution?: 'none' | 'author'
  }): Promise<{
    forward_id: string
    destination_room_id: string
    snapshot: {
      body: string
      msgtype: string
      original_sender_display: string
    }
  }> {
    checkAuth()
    return httpPost(`${MATRIX_CHAT_API}/forward/prepare/`, {
      attribution: 'author',
      ...payload,
    })
  }

  static async completeForward(payload: {
    forward_id: string
    destination_event_id?: string
    error?: string
  }): Promise<void> {
    checkAuth()
    await httpPost(`${MATRIX_CHAT_API}/forward/complete/`, payload)
  }
}
